import { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNotification } from "../../../../../Hooks/useNotification";

const AccessSettingForm = ({ formData, onDataChange }) => {
  const [accessSettings, setAccessSettings] = useState({
    password: "",
    confirmPassword: "",
    status: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const showNotification = useNotification();

  const handleAccessSettingsChange = (event) => {
    const { name, value } = event.target;

    if (name === "status") {
      const newValue = event.target.checked;
      onDataChange(name, newValue);
    } else {
      onDataChange(name, value);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateForm = () => {
    const rules = [
      {
        condition: !accessSettings.password,
        message: "New Password is required",
      },
      {
        condition:
          accessSettings.password && !validatePassword(accessSettings.password),
        message:
          "Password must meet the following requirements:\n" +
          "- At least 8 characters long\n" +
          "- One uppercase letter\n" +
          "- One lowercase letter\n" +
          "- One number\n" +
          "- One special character (e.g., @$!%*?&)\n" +
          "Example: P@ssw0rd",
        title: "Invalid password!",
        duration: 6000,
      },
      {
        condition: !accessSettings.confirmPassword,
        message: "Confirm Password is required",
      },
      {
        condition:
          accessSettings.password &&
          accessSettings.confirmPassword &&
          accessSettings.password !== accessSettings.confirmPassword,
        message: "Passwords do not match",
      },
    ];

    for (const rule of rules) {
      if (rule.condition) {
        showNotification("error", rule.message, rule.title, rule.duration);
        return false;
      }
    }
    return true;
  };

  const handlePasswordSave = (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    console.log("Password Changed:", accessSettings.password);
  };

  const handleManageAccess = () => {
    console.log(
      "Manage User Access Clicked. User Disabled Status:",
      accessSettings.isUserDisabled
    );
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={7}>
        <Typography variant="h6" textAlign="left" sx={{ marginBottom: "24px" }}>
          Change Password
        </Typography>
        <Box
          component="form"
          onSubmit={handlePasswordSave}
          noValidate
          sx={{ mt: 2 }}
        >
          <Typography
            component="div"
            color="secondary"
            variant="caption"
            sx={{ my: 1, fontStyle: "italic" }}
          >
            Fields marked * are required
          </Typography>

          <TextField
            required
            fullWidth
            id="password"
            label="New Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleAccessSettingsChange}
            variant="outlined"
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: formData.password ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    tabIndex={-1} // Prevents focus on tab click
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            required
            fullWidth
            id="confirmPassword"
            label="Confirm Password"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={handleAccessSettingsChange}
            variant="outlined"
            sx={{ mb: 4 }}
            InputProps={{
              endAdornment: formData.confirmPassword ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-start",
        }}
      >
        <Paper
          variant="outlined"
          sx={{ p: 2, backgroundColor: "#F7F7F7", borderRadius: 1 }}
        >
          <Typography variant="h6" gutterBottom textAlign="left">
            Manage User Access
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={handleAccessSettingsChange}
                  name="status"
                />
              }
              label={
                formData.status ? "The User is Enabled" : "The User is Disabled"
              }
              sx={{ mb: 1 }}
            />
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: "14",
              fontWeight: 500,
              color: "#5D6679",
            }}
            textAlign="left"
          >
            If disabled, the user will unfortunately not have access to the
            system or any of the connected devices.
          </Typography>
          {/* <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#7152F3", textTransform: "none" }}
              onClick={handleManageAccess}
            >
              Manage
            </Button>
          </Box> */}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AccessSettingForm;
